import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { palette, lightPalette, shape } from '@app/theme';
import { MarkdownContent } from '@components/ui/MarkdownContent';

import type { TeamMemberCardProperties } from './TeamMemberCard.types';

export const TeamMemberCard: React.FC<TeamMemberCardProperties> = ({
  member,
  dataTest,
}) => {
  const avatarBg = member.is_founder
    ? { backgroundColor: palette.primary.main, color: 'primary.contrastText' }
    : { backgroundColor: lightPalette.background.default, color: lightPalette.text.secondary };

  return (
    <Box
      data-testid={dataTest}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        border: `1px solid ${lightPalette.border.light}`,
        borderRadius: `${shape.borderRadius}px`,
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: lightPalette.border.default },
      }}
    >
      {/* Avatar */}
      <Box
        data-testid={dataTest ? `${dataTest}_avatar` : undefined}
        sx={{
          width: 40,
          height: 40,
          mt: 0.25,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...avatarBg,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {member.initials}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          data-testid={dataTest ? `${dataTest}_name` : undefined}
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: lightPalette.text.primary,
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
          }}
        >
          {member.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: lightPalette.text.muted,
            lineHeight: 1.4,
            mt: '2px',
          }}
        >
          {member.role}
        </Typography>
        {member.details && (
          <MarkdownContent
            variant='compact'
            sx={{ fontSize: 12, color: lightPalette.text.secondary, mt: 0.5 }}
          >
            {member.details}
          </MarkdownContent>
        )}
      </Box>
    </Box>
  );
};
